import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, Button, Card, ConfirmDialog, Field, InfoTip, MonthPicker, PillButton, apiErrorMessage, formatMoney, brandLocalDate, rebaseDatePrefill, useBrand } from '@aralere/shared-ui';
import type {
  Business,
  Client,
  CreateMileageRequest,
  ListMileageResponse,
  MileageEntry,
  MileageRateDefaults,
  MileageRateSource,
  ProjectWithTotals,
  UpdateMileageRequest,
} from '@aralere/shared-types';

// Mileage admin (spec §10.5; Stage-3 slice 4 — s43 record §8, Q43). Mileage is partitioned by BRAND
// (businessId), so the page is brand-scoped: pick a brand (the picker collapses to nothing when only
// one brand exists — the single-tenant lens), pick a month/year, optionally filter to one client,
// and log mileage. A logged entry can be tied to a client (its override drives the billed rate) or
// left general. The billed rate resolves by the Q43 precedence (client override -> brand rate ->
// margin-over-federal -> federal); the server snapshots irsRate + billedRatePerMile onto each entry.
// The form previews the effective rate client-side, mirroring the server. Required fields carry the
// red asterisk via the shared Field (the platform standard). Mobile-first: entries render as stacked
// cards under md and as a table from md up. Billed entries are locked.

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const inputClass = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';

const round2 = (n: number): number => Math.round(n * 100) / 100;
const round3 = (n: number): number => Math.round(n * 1000) / 1000;
const ceilCent = (n: number): number => Math.ceil(n * 100 - 1e-9) / 100;
const fmtRate = (n: number): string => `$${n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '.0')}`;
const fmtMoney = formatMoney; // shared thousands-separated formatter (s58)
const fmtMiles = (n: number): string => (Number.isInteger(n) ? String(n) : String(n));

/** YYYY-MM -> "July 2026". */
function monthLabel(month: string): string {
  const m = parseInt(month.slice(5, 7), 10);
  return `${MONTH_NAMES[(m - 1 + 12) % 12] ?? month} ${month.slice(0, 4)}`;
}
/** YYYY-MM-DD -> MM-DD-YYYY. */
function displayDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${m}-${d}-${y}`;
}

const RATE_SOURCE_LABEL: Record<MileageRateSource, string> = {
  'client-override': 'client override',
  'brand-rate': "this brand's rate",
  'margin-over-federal': 'margin over IRS',
  federal: 'IRS rate',
};

/** Client-side mirror of the server's Q43 rate resolution (preview only; the server is authoritative). */
function effectiveRate(
  defaults: MileageRateDefaults,
  client: Client | null,
): { rate: number; source: MileageRateSource; federal: number } {
  const federal = defaults.federalRate ?? 0;
  const override = client?.mileageRateOverride ?? null;
  if (override != null) return { rate: round3(override), source: 'client-override', federal };
  if (defaults.brandRatePerMile != null) return { rate: round3(defaults.brandRatePerMile), source: 'brand-rate', federal };
  if (defaults.federalRate != null && defaults.marginEnabled && defaults.marginRate > 0 && defaults.marginRate < 1) {
    return { rate: ceilCent(defaults.federalRate / (1 - defaults.marginRate)), source: 'margin-over-federal', federal };
  }
  return { rate: round3(federal), source: 'federal', federal };
}

interface FormState {
  clientId: string; // '' = general (no client)
  projectId: string; // '' = none (slice 6); only meaningful with a client — a project belongs to a client
  date: string;
  reason: string;
  startLocation: string;
  endLocation: string;
  miles: string;
  billable: boolean;
}

// 04 #136: the trip date prefills to today on the brand clock of the page's brand (02 s177).
const emptyForm = (timezone: string | null): FormState => ({
  clientId: '',
  projectId: '',
  date: brandLocalDate(timezone),
  reason: '',
  startLocation: '',
  endLocation: '',
  miles: '',
  billable: true,
});

function formFromEntry(e: MileageEntry): FormState {
  return {
    clientId: e.clientId ?? '',
    projectId: e.projectId ?? '',
    date: e.date,
    reason: e.reason,
    startLocation: e.startLocation,
    endLocation: e.endLocation,
    miles: String(e.miles),
    billable: e.billable,
  };
}

export function AdminMileageEntries(): JSX.Element {
  const { brand: portalBrand } = useBrand();
  const portalTz = portalBrand?.timezone ?? null;
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [businessId, setBusinessId] = useState('');
  const [clientFilter, setClientFilter] = useState(''); // '' = all of the brand
  const [projectsByClient, setProjectsByClient] = useState<Record<string, ProjectWithTotals[]>>({}); // slice 6 picker cache
  // 04 #136: this month on the page brand's clock (the portal brand's until the page brand loads).
  const [month, setMonth] = useState<string>(() => brandLocalDate(portalTz).slice(0, 7));
  /** The zone the month default and an open create-form's date were last computed in. */
  const prefillTzRef = useRef<string | null>(portalTz);
  const [data, setData] = useState<ListMileageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  // Invoice-line deep link (s77 #16b): ?client= scopes the list; ?focus=<id> scrolls to + highlights.
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  useEffect(() => {
    const c = searchParams.get('client');
    if (c) setClientFilter(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!focusId || loading) return;
    const el = document.getElementById(`src-${focusId}`) ?? document.getElementById(`src-m-${focusId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusId, loading, data]);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MileageEntry | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MileageEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Load brands + clients once. Default the brand to the first (the picker collapses when only one).
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [b, c] = await Promise.all([api.admin.listBusinesses(), api.admin.listClients()]);
        if (!alive) return;
        setBusinesses(b.businesses);
        setClients(c.clients.map((r) => r.client));
        if (b.businesses.length > 0) setBusinessId((cur) => cur || b.businesses[0].id);
      } catch (e) {
        if (alive) setError(apiErrorMessage(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const load = async (bid: string, m: string, cid: string): Promise<void> => {
    if (!bid) return;
    setLoading(true);
    setError(null);
    try {
      setData(await api.admin.listMileage(bid, m, undefined, cid || undefined));
    } catch (e) {
      setError(apiErrorMessage(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId) void load(businessId, month, clientFilter);
    else setData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, month, clientFilter]);

  /** 04 #136: the page brand's timezone (its `timezone`, absent = the platform default); with no
   *  page brand yet, the portal brand's. A new entry belongs to the page brand. */
  const pageTz = (id: string): string | null => {
    const b = businesses.find((x) => x.id === id);
    return b ? b.timezone ?? null : portalTz;
  };

  // 04 #136: when the page brand is set or switched (or the portal brand arrives first), an untouched
  // month default and an open NEW entry's untouched date follow its clock; an edit form never moves.
  useEffect(() => {
    const from = prefillTzRef.current;
    const to = pageTz(businessId);
    if (from === to) return;
    prefillTzRef.current = to;
    setMonth((m) => rebaseDatePrefill(m, from, to));
    if (!editing) setForm((f) => (f ? { ...f, date: rebaseDatePrefill(f.date, from, to) } : f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, businesses, portalTz]);

  // The brand's own clients (the form + filter only ever offer these, keeping businessId consistent).
  const brandClients = useMemo(
    () => clients.filter((c) => c.primaryBusinessId === businessId && c.status !== 'archived'),
    [clients, businessId],
  );
  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  const clientLabel = (id: string | null): string => (id ? clientById.get(id)?.displayName ?? 'Unknown client' : 'General');

  const onPickMonth = (value: string): void => {
    if (value) setMonth(value);
  };

  const update = (patch: Partial<FormState>): void => setForm((f) => (f ? { ...f, ...patch } : f));

  const openCreate = (): void => {
    setEditing(null);
    setForm({ ...emptyForm(pageTz(businessId)), clientId: clientFilter || '' });
    setFormError(null);
    setFormOpen(true);
  };
  const openEdit = (e: MileageEntry): void => {
    setEditing(e);
    setForm(formFromEntry(e));
    setFormError(null);
    setFormOpen(true);
  };
  const closeForm = (): void => {
    setFormOpen(false);
    setForm(null);
    setEditing(null);
    setFormError(null);
  };

  // Slice 6: lazy-load the form client's projects for the Project picker (cached per client;
  // best-effort — a projects hiccup must not block mileage logging).
  const formClientId = form?.clientId ?? '';
  useEffect(() => {
    if (!formClientId || projectsByClient[formClientId]) return;
    let alive = true;
    void (async () => {
      try {
        const res = await api.admin.listProjects(formClientId);
        if (alive) setProjectsByClient((m) => ({ ...m, [formClientId]: res.projects }));
      } catch {
        if (alive) setProjectsByClient((m) => ({ ...m, [formClientId]: [] }));
      }
    })();
    return () => {
      alive = false;
    };
  }, [formClientId, projectsByClient]);

  // s72 (owner UX): the LIST also shows each entry's project name under the client, so pre-fill
  // the same per-client cache for every client that appears in the loaded month with a project
  // link. Best-effort — a projects hiccup only costs the label, never the list.
  useEffect(() => {
    if (!data) return;
    const wanted = [
      ...new Set(
        data.entries
          .filter((e) => e.projectId && e.clientId)
          .map((e) => e.clientId as string),
      ),
    ].filter((cid) => !projectsByClient[cid]);
    if (wanted.length === 0) return;
    let alive = true;
    void (async () => {
      for (const cid of wanted) {
        try {
          const res = await api.admin.listProjects(cid);
          if (alive) setProjectsByClient((m) => ({ ...m, [cid]: res.projects }));
        } catch {
          if (alive) setProjectsByClient((m) => ({ ...m, [cid]: [] }));
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [data, projectsByClient]);

  /** The project name for a list row, or null (no link / name not loaded yet). */
  const projectLabel = (e: { clientId: string | null; projectId: string | null }): string | null => {
    if (!e.projectId || !e.clientId) return null;
    return projectsByClient[e.clientId]?.find((p) => p.id === e.projectId)?.name ?? null;
  };

  // Live preview of the effective billed rate for the form's selected client.
  const formRate = useMemo(() => {
    if (!form || !data) return null;
    const client = form.clientId ? clientById.get(form.clientId) ?? null : null;
    const r = effectiveRate(data.defaults, client);
    const miles = parseFloat(form.miles);
    const validMiles = Number.isFinite(miles) && miles > 0;
    return {
      ...r,
      cost: validMiles ? round2(miles * r.federal) : null,
      charge: validMiles ? (form.clientId && form.billable ? round2(miles * r.rate) : 0) : null,
    };
  }, [form, data, clientById]);

  const doSave = async (): Promise<void> => {
    if (!form || !data) return;
    const miles = parseFloat(form.miles);
    if (!form.reason.trim()) return setFormError('A reason or service call is required.');
    if (!form.startLocation.trim() || !form.endLocation.trim()) return setFormError('Both a start and an end location are required.');
    if (!form.miles.trim() || !Number.isFinite(miles) || miles <= 0) return setFormError('Enter a positive number of miles.');
    if (Math.abs(miles * 100 - Math.round(miles * 100)) > 1e-6) return setFormError('Miles can have at most 2 decimals.');
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        const body: UpdateMileageRequest = {
          clientId: form.clientId || null,
          projectId: form.clientId && form.projectId ? form.projectId : null,
          date: form.date,
          reason: form.reason.trim(),
          startLocation: form.startLocation.trim(),
          endLocation: form.endLocation.trim(),
          miles: Math.round(miles * 100) / 100,
          billable: form.clientId ? form.billable : false,
        };
        await api.admin.updateMileage(editing.id, editing.businessId, body);
      } else {
        const body: CreateMileageRequest = {
          businessId: data.businessId,
          clientId: form.clientId || null,
          projectId: form.clientId && form.projectId ? form.projectId : null,
          date: form.date,
          reason: form.reason.trim(),
          startLocation: form.startLocation.trim(),
          endLocation: form.endLocation.trim(),
          miles: Math.round(miles * 100) / 100,
          billable: form.clientId ? form.billable : false,
        };
        await api.admin.createMileage(body);
      }
      closeForm();
      await load(businessId, month, clientFilter);
    } catch (e) {
      setFormError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.admin.deleteMileage(deleteTarget.id, deleteTarget.businessId);
      setDeleteTarget(null);
      await load(businessId, month, clientFilter);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  // Group the loaded span into month sections (newest first) + span totals.
  const monthGroups = useMemo(() => {
    if (!data) return [] as { month: string; entries: MileageEntry[] }[];
    const by = new Map<string, MileageEntry[]>();
    for (const e of data.entries) {
      const k = e.date.slice(0, 7);
      const arr = by.get(k);
      if (arr) arr.push(e);
      else by.set(k, [e]);
    }
    return [...by.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1)).map(([m, entries]) => ({ month: m, entries }));
  }, [data]);

  const totals = useMemo(() => {
    let miles = 0;
    let cost = 0;
    let charge = 0;
    if (data) {
      for (const e of data.entries) {
        miles += e.miles;
        cost += e.cost;
        charge += e.charge;
      }
    }
    return { miles: round2(miles), cost: round2(cost), charge: round2(charge) };
  }, [data]);

  const noRateConfigured = data && data.defaults.federalRate == null && data.defaults.brandRatePerMile == null;
  const spanLabel = data?.year ? data.year : monthLabel(month);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl">Mileage</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Log business mileage per brand, optionally tied to a client. The IRS rate is your deduction
            basis; billable mileage bills at the resolved per-mile rate.
          </p>
        </div>
        <Button onClick={openCreate} disabled={!businessId}>Log mileage</Button>
      </div>

      {/* Controls: brand (collapses when one) + month + client filter. `relative z-20` lifts this
          glass card's stacking context above the later cards so the MonthPicker popover (which
          overflows downward) isn't painted under the totals card — the glass backdrop-filter makes
          each Card its own stacking context, trapping the popover's own z-index otherwise. */}
      <Card className="relative z-20">
        <div className="grid gap-4 md:grid-cols-3">
          {businesses.length > 1 ? (
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">Brand</span>
              <select
                className={inputClass}
                value={businessId}
                onChange={(e) => {
                  setBusinessId(e.target.value);
                  setClientFilter('');
                }}
              >
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium">Month</span>
            <MonthPicker value={month} onChange={onPickMonth} className={`${inputClass} text-left`} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium">Client</span>
            <select className={inputClass} value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
              <option value="">All (brand + general)</option>
              {brandClients.map((c) => (
                <option key={c.id} value={c.id}>{c.displayName}</option>
              ))}
            </select>
          </label>
        </div>
        {noRateConfigured ? (
          <p className="mt-3 text-xs text-[var(--tint-warn-text)]">
            No mileage rate is configured for this brand yet. Set the IRS rate (and optionally a brand
            rate) on the Settings page so mileage books a deduction and a billed rate.
          </p>
        ) : null}
      </Card>

      {error ? <div className="rounded-md border border-[var(--tint-red-border)] bg-[var(--tint-red-bg-soft)] p-3 text-sm text-[var(--tint-red-text)]">{error}</div> : null}

      {/* Span totals. */}
      {data ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">{data.year ? `${data.year} total` : monthLabel(data.month)}</p>
              <p className="text-xs text-[var(--color-muted)]">
                {clientFilter ? clientLabel(clientFilter) : 'All of the brand'}
              </p>
            </div>
            <div className="flex flex-wrap gap-6 text-sm">
              <div><span className="text-[var(--color-muted)]">Miles </span><span className="font-medium">{fmtMiles(totals.miles)}</span></div>
              <div>
                <span className="text-[var(--color-muted)]">Deduction </span>
                <span className="font-medium">{fmtMoney(totals.cost)}</span>
                <InfoTip text="Your IRS-rate mileage deduction (miles times the IRS rate), including non-billable mileage." />
              </div>
              <div><span className="text-[var(--color-muted)]">Billable </span><span className="font-medium">{fmtMoney(totals.charge)}</span></div>
            </div>
          </div>
        </Card>
      ) : null}

      {/* Log / edit form. */}
      {formOpen && form ? (
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-xl">{editing ? 'Edit mileage' : 'Log mileage'}</h2>
            <button type="button" className="text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]" onClick={closeForm}>Close</button>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Date" required>
              <input type="date" className={inputClass} value={form.date} onChange={(e) => update({ date: e.target.value })} />
            </Field>
            <Field label="Client" tip="Optional. Tying mileage to a client applies that client's rate override and lets it be billed to them. Leave as general for internal or overhead mileage (deduction only, never billed).">
              <select className={inputClass} value={form.clientId} onChange={(e) => { const cid = e.target.value; update(cid ? { clientId: cid, projectId: '' } : { clientId: cid, projectId: '', billable: false }); }}>
                <option value="">General</option>
                {brandClients.map((c) => (
                  <option key={c.id} value={c.id}>{c.displayName}</option>
                ))}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Reason / service call" required>
                <input className={inputClass} value={form.reason} onChange={(e) => update({ reason: e.target.value })} placeholder="On-site service call" />
              </Field>
            </div>
            <Field label="Start location" required>
              <input className={inputClass} value={form.startLocation} onChange={(e) => update({ startLocation: e.target.value })} placeholder="Office" />
            </Field>
            <Field label="End location" required>
              <input className={inputClass} value={form.endLocation} onChange={(e) => update({ endLocation: e.target.value })} placeholder="Client HQ" />
            </Field>
            <Field label="Miles" required>
              <input type="number" step="any" min="0" className={inputClass} value={form.miles} onChange={(e) => update({ miles: e.target.value })} />
            </Field>
            {form.clientId ? (
              <Field label="Project" tip="Optional. Ties this mileage to one of the client's projects so the project's totals and (Stage 4) 'Generate Invoice from Project' pick it up. Only active projects accept new entries.">
                <select className={inputClass} value={form.projectId} onChange={(e) => update({ projectId: e.target.value })}>
                  <option value="">None</option>
                  {(projectsByClient[form.clientId] ?? [])
                    .filter((p) => p.status === 'active' || p.id === form.projectId)
                    .map((p) => (
                      <option key={p.id} value={p.id}>{p.name}{p.status !== 'active' ? ` (${p.status === 'on-hold' ? 'on hold' : p.status})` : ''}</option>
                    ))}
                </select>
              </Field>
            ) : null}
            {form.clientId ? (
              <label className="flex items-center gap-2 self-end pb-2 text-sm">
                <input type="checkbox" checked={form.billable} onChange={(e) => update({ billable: e.target.checked })} />
                Billable
                <InfoTip text="Non-billable mileage is excluded from invoices but still counts toward your IRS mileage deduction." />
              </label>
            ) : (
              <div className="self-end pb-2 text-xs text-[var(--color-muted)]">
                General mileage is non-billable (there is no client to bill). It still counts toward your IRS deduction.
              </div>
            )}
          </div>

          {formRate ? (
            <div className="mt-4 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-3 text-sm">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <div>
                  <span className="text-[var(--color-muted)]">Billed rate </span>
                  <span className="font-medium">{fmtRate(formRate.rate)}/mi</span>
                  <span className="text-xs text-[var(--color-muted)]"> ({RATE_SOURCE_LABEL[formRate.source]})</span>
                </div>
                {formRate.charge != null ? (
                  <div><span className="text-[var(--color-muted)]">Charge </span><span className="font-medium">{form.billable ? fmtMoney(formRate.charge) : '$0.00 (non-billable)'}</span></div>
                ) : null}
                {formRate.cost != null ? (
                  <div><span className="text-[var(--color-muted)]">Deduction </span><span className="font-medium">{fmtMoney(formRate.cost)}</span></div>
                ) : null}
              </div>
              <p className="mt-1 text-xs text-[var(--color-muted)]">The rate is snapshotted when you save; later Settings changes never rewrite saved entries.</p>
            </div>
          ) : null}

          {formError ? <p className="mt-3 text-sm text-[var(--tint-red-text)]">{formError}</p> : null}
          <div className="mt-4 flex gap-2">
            <Button onClick={doSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Save changes' : 'Log mileage'}</Button>
            <Button variant="ghost" onClick={closeForm} disabled={saving}>Cancel</Button>
          </div>
        </Card>
      ) : null}

      {loading ? <p className="text-sm text-[var(--color-muted)]">Loading {spanLabel}...</p> : null}

      {!loading && data && data.entries.length === 0 ? (
        <Card><p className="text-sm text-[var(--color-muted)]">No mileage logged for {spanLabel}.</p></Card>
      ) : null}

      {monthGroups.map((group) => (
        <div key={group.month} className="space-y-2">
          <h2 className="font-display text-lg">{monthLabel(group.month)}</h2>

          {/* Table from md up. */}
          <div className="hidden overflow-hidden rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] md:block">
            <table className="w-full text-sm">
              <thead className="bg-[rgb(var(--edge-rgb)/0.03)] text-left text-xs text-[var(--color-muted)]">
                <tr>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Reason / route</th>
                  <th className="px-3 py-2 text-right font-medium">Miles</th>
                  <th className="px-3 py-2 text-right font-medium">Rate</th>
                  <th className="px-3 py-2 text-right font-medium">Charge</th>
                  <th className="px-3 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {group.entries.map((e) => (
                  <tr key={e.id} id={`src-${e.id}`} className={`border-t border-[rgb(var(--edge-rgb)/0.05)] scroll-mt-24 ${focusId === e.id ? 'bg-[var(--color-primary)]/10' : ''}`}>
                    <td className="whitespace-nowrap px-3 py-2">{displayDate(e.date)}</td>
                    <td className="px-3 py-2">
                      {clientLabel(e.clientId)}
                      {projectLabel(e) ? (
                        <div className="text-xs text-[var(--color-muted)]">{projectLabel(e)}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{e.reason}</div>
                      <div className="text-xs text-[var(--color-muted)]">{e.startLocation} to {e.endLocation}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">{fmtMiles(e.miles)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">{fmtRate(e.billedRatePerMile)}/mi</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {e.billable ? fmtMoney(e.charge) : <span className="text-xs text-[var(--color-muted)]">non-billable</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        {e.billed ? (
                          <span className="text-xs text-[var(--color-muted)]">billed</span>
                        ) : (
                          <>
                            <PillButton onClick={() => openEdit(e)}>Edit</PillButton>
                            <PillButton tone="red" onClick={() => setDeleteTarget(e)}>Delete</PillButton>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Stacked cards under md. */}
          <div className="space-y-2 md:hidden">
            {group.entries.map((e) => (
              <div key={e.id} id={`src-m-${e.id}`} className={`rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] p-3 scroll-mt-24 ${focusId === e.id ? 'ring-2 ring-[var(--color-primary)]' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-medium">{e.reason}</div>
                    <div className="text-xs text-[var(--color-muted)]">{displayDate(e.date)} - {clientLabel(e.clientId)}</div>
                    {projectLabel(e) ? (
                      <div className="text-xs text-[var(--color-muted)]">{projectLabel(e)}</div>
                    ) : null}
                  </div>
                  <div className="text-right text-sm">
                    <div className="font-medium">{e.billable ? fmtMoney(e.charge) : 'non-billable'}</div>
                    <div className="text-xs text-[var(--color-muted)]">{fmtMiles(e.miles)} mi</div>
                  </div>
                </div>
                <div className="mt-1 text-xs text-[var(--color-muted)]">{e.startLocation} to {e.endLocation} - {fmtRate(e.billedRatePerMile)}/mi</div>
                <div className="mt-2 flex gap-2">
                  {e.billed ? (
                    <span className="text-xs text-[var(--color-muted)]">billed - locked</span>
                  ) : (
                    <>
                      <PillButton onClick={() => openEdit(e)}>Edit</PillButton>
                      <PillButton tone="red" onClick={() => setDeleteTarget(e)}>Delete</PillButton>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {deleteTarget ? (
        <ConfirmDialog
          open={true}
          title="Delete mileage entry?"
          confirmLabel="Delete"
          confirmVariant="danger"
          busy={deleting}
          onConfirm={doDelete}
          onCancel={() => setDeleteTarget(null)}
        >
          <p>
            Delete the {fmtMiles(deleteTarget.miles)}-mile entry from {displayDate(deleteTarget.date)}
            {deleteTarget.clientId ? ` for ${clientLabel(deleteTarget.clientId)}` : ''}? This cannot be undone.
          </p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
